import { METRICS, type MetricKey } from '@integrate/core';

/*
  Headcount / FTE / Cost, in one place.

  Two shipped panes spelled the same control two ways — a button group here, a hand-typed `<select>`
  there — and one of them shortened its label from "Loaded cost" to "Cost" while the other's Cost is raw
  salary. Same word, two weights, two tabs of one modal, which is the P4l-a defect in label form.

  So the LABEL CARRIES THE WEIGHT. `weight` is not decoration: a caller measuring in loaded cost has to
  say so on the button, because that is the only place a reader comparing two tabs will look. The labels
  themselves come from core's `METRICS`, never retyped.
*/

export function MetricToggle({
  value,
  onChange,
  keys = ['hc', 'fte', 'cost'],
  weight,
  testIdPrefix,
  label = 'Measured in',
}: {
  value: MetricKey;
  onChange(next: MetricKey): void;
  /** Which of the three this surface offers, in order. */
  keys?: readonly MetricKey[];
  /** What "Cost" means here — 'raw' salary or 'loaded'. Shown on the button, not only in a footnote. */
  weight: 'raw' | 'loaded';
  testIdPrefix: string;
  label?: string;
}): JSX.Element {
  const labelOf = (k: MetricKey): string =>
    k === 'cost' ? (weight === 'loaded' ? 'Loaded cost' : 'Cost (raw)') : METRICS[k].label;

  return (
    <div className="row" style={{ alignItems: 'center', gap: 12 }}>
      <span className="muted small">{label}</span>
      <div className="tabs" role="group" aria-label={label}>
        {keys.map((k) => (
          <button
            key={k}
            className={`toggle-btn${value === k ? ' active' : ''}`}
            aria-pressed={value === k}
            data-testid={`${testIdPrefix}-${k}`}
            onClick={() => onChange(k)}
          >
            {labelOf(k)}
          </button>
        ))}
      </div>
    </div>
  );
}
